import React, { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Mail, Lock, AlertCircle } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import Logo from "../components/Logo";
import { useAuth, formatApiErrorDetail } from "../context/AuthContext";

export default function Login() {
  const { t } = useTranslation();
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      const next = location.state?.from || "/dashboard";
      navigate(next, { replace: true });
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[90vh] flex items-center justify-center py-12 relative overflow-hidden bg-white" data-testid="login-page">
      <span className="geo-shape bg-[#16A34A]/15 h-72 w-72 -top-20 -right-20" aria-hidden />
      <span className="geo-shape bg-[#16A34A]/10 h-64 w-64 -bottom-20 -left-20" aria-hidden />

      <div className="relative w-full max-w-md mx-auto px-4">
        <Link to="/" className="flex items-center justify-center mb-8">
          <Logo size={42} />
        </Link>

        <div className="bg-white rounded-3xl border border-[#E5EBE7] shadow-xl p-8">
          <h1 className="font-display text-3xl font-bold text-[#0A2518]">{t("auth.welcomeBack")}</h1>
          <p className="mt-2 text-[#4B5563]">{t("auth.loginSubtitle")}</p>

          <form onSubmit={onSubmit} className="mt-6 space-y-5">
            <div>
              <Label htmlFor="email" className="text-[#0A2518]">{t("auth.email")}</Label>
              <div className="relative mt-1.5">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#4B5563]" />
                <Input
                  id="email"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="pl-9 h-11 focus-visible:ring-[#16A34A]"
                  data-testid="login-email-input"
                />
              </div>
            </div>
            <div>
              <Label htmlFor="password" className="text-[#0A2518]">{t("auth.password")}</Label>
              <div className="relative mt-1.5">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#4B5563]" />
                <Input
                  id="password"
                  type="password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="pl-9 h-11 focus-visible:ring-[#16A34A]"
                  data-testid="login-password-input"
                />
              </div>
            </div>

            {error && (
              <div className="flex items-start gap-2 text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2" data-testid="login-error">
                <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
                <span>{error}</span>
              </div>
            )}

            <Button
              type="submit"
              disabled={loading}
              className="w-full h-12 rounded-full bg-[#16A34A] hover:bg-[#15803D] text-white"
              data-testid="login-submit-btn"
            >
              {loading ? t("auth.loginLoading") : t("auth.loginBtn")}
            </Button>
          </form>

          <p className="mt-6 text-sm text-center text-[#4B5563]">
            {t("auth.noAccountYet")}{" "}
            <Link to="/register" className="text-[#16A34A] font-medium hover:underline" data-testid="login-go-register">
              {t("auth.createAccount")}
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
